import React, { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getIntegratorMe, integratorApi, integratorLogin } from "../services/api/integrator/integratorAuthApi";

export const IntegratorAuthContext = createContext();

const STORAGE_KEYS = {
  user: "integratorUser",
  token: "integratorToken",
  activeCompanyId: "integratorActiveCompanyId",
};

const getCompanyIdFromNotificationLink = (companies = []) => {
  const currentUrl = new URL(window.location.href);
  const requestedCompanyId = Number(currentUrl.searchParams.get("clientId"));

  if (!Number.isInteger(requestedCompanyId) || requestedCompanyId <= 0) {
    return null;
  }

  if (!companies.some((company) => Number(company.id) === requestedCompanyId)) {
    return null;
  }

  currentUrl.searchParams.delete("clientId");
  window.history.replaceState(window.history.state, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);

  return requestedCompanyId;
};

export const IntegratorAuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(null);
  const [activeCompanyId, setActiveCompanyIdState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const recoveredUser = localStorage.getItem(STORAGE_KEYS.user);
    const token = localStorage.getItem(STORAGE_KEYS.token);
    const storedCompanyId = localStorage.getItem(STORAGE_KEYS.activeCompanyId);

    if (recoveredUser && token) {
      const parsedUser = JSON.parse(recoveredUser);
      const notificationCompanyId = getCompanyIdFromNotificationLink(parsedUser.companies);
      const initialCompanyId = notificationCompanyId
        || (storedCompanyId ? Number(storedCompanyId) : null)
        || parsedUser.activeIntegratorId
        || parsedUser.companies?.[0]?.id
        || null;

      integratorApi.defaults.headers.common.Authorization = `Bearer ${token}`;
      setAuth(parsedUser);
      setActiveCompanyIdState(initialCompanyId);

      if (initialCompanyId) {
        localStorage.setItem(STORAGE_KEYS.activeCompanyId, String(initialCompanyId));
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const interceptorId = integratorApi.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }

        return Promise.reject(error);
      }
    );

    return () => integratorApi.interceptors.response.eject(interceptorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await integratorLogin(email, password);
    const loggedUser = data.user;
    const token = data.token;
    const defaultCompanyId = loggedUser.activeIntegratorId || loggedUser.companies?.[0]?.id || null;

    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(loggedUser));
    localStorage.setItem(STORAGE_KEYS.token, token);

    if (defaultCompanyId) {
      localStorage.setItem(STORAGE_KEYS.activeCompanyId, String(defaultCompanyId));
    }

    integratorApi.defaults.headers.common.Authorization = `Bearer ${token}`;
    setAuth(loggedUser);
    setActiveCompanyIdState(defaultCompanyId);
    navigate("/integrador/dashboard");
  };

  const refreshMe = async () => {
    const { data } = await getIntegratorMe();
    const refreshedUser = data.user;
    const nextCompanyId = activeCompanyId || refreshedUser.activeIntegratorId || refreshedUser.companies?.[0]?.id || null;

    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(refreshedUser));
    if (nextCompanyId) {
      localStorage.setItem(STORAGE_KEYS.activeCompanyId, String(nextCompanyId));
    }

    setAuth(refreshedUser);
    setActiveCompanyIdState(nextCompanyId);
  };

  const setActiveCompanyId = (companyId) => {
    const numericCompanyId = Number(companyId);
    localStorage.setItem(STORAGE_KEYS.activeCompanyId, String(numericCompanyId));
    setActiveCompanyIdState(numericCompanyId);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.activeCompanyId);
    integratorApi.defaults.headers.common.Authorization = null;
    setAuth(null);
    setActiveCompanyIdState(null);
    navigate("/integrador/login");
  };

  const activeCompany = auth?.companies?.find((company) => company.id === activeCompanyId) || auth?.companies?.[0] || null;

  return (
    <IntegratorAuthContext.Provider
      value={{
        authenticated: !!auth,
        auth,
        activeCompany,
        activeCompanyId,
        loading,
        login,
        logout,
        refreshMe,
        setActiveCompanyId,
      }}
    >
      {children}
    </IntegratorAuthContext.Provider>
  );
};
